import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const STORE_PREFIX = "brainzr.scroll.";
const MAX_KEYS = 32;

function read(key: string): number | null {
  try {
    const raw = sessionStorage.getItem(STORE_PREFIX + key);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function write(key: string, value: number) {
  try {
    sessionStorage.setItem(STORE_PREFIX + key, String(value));
    // Best-effort cap so we don't accumulate sessionStorage entries forever
    // across long-lived tabs.
    const own = Object.keys(sessionStorage).filter((k) => k.startsWith(STORE_PREFIX));
    if (own.length > MAX_KEYS) {
      own.slice(0, own.length - MAX_KEYS).forEach((k) => sessionStorage.removeItem(k));
    }
  } catch {
    /* quota / privacy mode — ignore */
  }
}

/** Save the window scroll position keyed to the current history entry, and
 *  restore it when the user navigates back to this entry. `ready` should
 *  flip true once the page's content (catalog grid, etc.) has rendered —
 *  restoring before the content exists would scroll into empty space. */
export function useScrollRestoration(ready: boolean = true) {
  const { key } = useLocation();
  useEffect(() => {
    if (!ready) return;
    const saved = read(key);
    if (saved !== null) {
      // Two rAFs: first lets layout settle, second runs after the browser
      // has painted at least one frame so big grids are measurable.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, saved);
        });
      });
    }
    const onScroll = () => write(key, window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      // Save final position on unmount too — covers fast click-then-back.
      write(key, window.scrollY);
      window.removeEventListener("scroll", onScroll);
    };
  }, [key, ready]);
}
